import {
  Badge,
  Button,
  Caption1,
  Combobox,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Subtitle2,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  BATCHES_PER_LINE_BONUS,
  LINE_BONUS_USD,
  PRODUCTION_LINES,
  payrollPeriodFor,
  shiftPayrollPeriod,
  todayInZone,
  type AmountEntryOptions,
  type ProductionLineCode,
  type ProductionLinesView,
} from '@worklogix/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTimeAdminApi } from '../api';
import { CenterModal } from '../components/CenterModal';

/**
 * Who is on each production line's crew, and what each line earned in a given week.
 *
 * Michael, 2026-10-28: "a modal or something where a supervisor can associate any
 * employee that is eligible for bonuses on any of the lines". Every Wednesday the job
 * posts each line's week to everyone listed here, so this list IS who gets paid.
 *
 * Michael, 2026-10-29: "I should be able to select previous weeks, then click the
 * production line crews button and see the total bonuses they had in previous weeks."
 * So it opens on the week the Bonus report is showing, and steps week by week.
 *
 * People are PICKED from the roster — the employee id is what is stored, never a name,
 * which is the whole lesson of the Excel import (6 of 29 typed names matched). A crew
 * membership lasts until someone presses Remove: an employee the directory sync has
 * deactivated stays listed, flagged, rather than silently disappearing.
 */

const useStyles = makeStyles({
  intro: { color: tokens.colorNeutralForeground3 },
  weekBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  weekLabel: { textAlign: 'center', fontVariantNumeric: 'tabular-nums' },
  line: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  lineHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  week: { color: tokens.colorNeutralForeground3, fontVariantNumeric: 'tabular-nums' },
  weekEarned: {
    color: tokens.colorPaletteGreenForeground1,
    fontWeight: tokens.fontWeightSemibold,
    fontVariantNumeric: 'tabular-nums',
  },
  members: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS },
  member: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    paddingLeft: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  empty: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  add: { minWidth: 0, maxWidth: '100%' },
  grid: { width: '100%', borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' },
  th: {
    textAlign: 'right',
    fontWeight: tokens.fontWeightSemibold,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    whiteSpace: 'nowrap',
  },
  thDate: { textAlign: 'left' },
  td: {
    textAlign: 'right',
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    whiteSpace: 'nowrap',
  },
  tdDate: { textAlign: 'left' },
  total: {
    fontWeight: tokens.fontWeightSemibold,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  earned: { color: tokens.colorPaletteGreenForeground1, fontWeight: tokens.fontWeightSemibold },
  noEnd: { color: tokens.colorPaletteMarigoldForeground1 },
  notRead: { color: tokens.colorNeutralForeground3 },
  gridWrap: { overflowX: 'auto' },
});

function money(value: number): string {
  return value.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

/** `2026-10-27` → `Tue 10/27`. */
function shortDate(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`);
  const dow = d.toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' });
  return `${dow} ${String(d.getUTCMonth() + 1)}/${String(d.getUTCDate())}`;
}

function addDays(iso: string, days: number): string {
  const ms = Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
  return new Date(ms + days * 86_400_000).toISOString().slice(0, 10);
}

export interface ProductionCrewsDialogProps {
  open: boolean;
  onClose: () => void;
  /** The Bonus report's employee list — the same roster a hand-typed bonus picks from. */
  options: AmountEntryOptions;
  /** The week to open on (any date in it). Omitted = the week in progress. */
  weekEnding?: string | undefined;
}

export function ProductionCrewsDialog({
  open,
  onClose,
  options,
  weekEnding,
}: ProductionCrewsDialogProps): JSX.Element {
  const styles = useStyles();
  const api = useTimeAdminApi();
  const currentWeek = useMemo(() => payrollPeriodFor(todayInZone(new Date())).endDate, []);
  const [week, setWeek] = useState<string>(weekEnding ?? currentWeek);
  const [view, setView] = useState<ProductionLinesView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [queries, setQueries] = useState<Partial<Record<ProductionLineCode, string>>>({});

  // Re-seed from the report's week every time the window opens.
  useEffect(() => {
    if (open) {
      setWeek(payrollPeriodFor(weekEnding ?? currentWeek).endDate);
    }
  }, [open, weekEnding, currentWeek]);

  const load = useCallback(async () => {
    try {
      setView(await api.getProductionLines(week));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the production lines.');
    }
  }, [api, week]);

  useEffect(() => {
    if (open) {
      setView(null);
      void load();
    }
  }, [open, load]);

  const run = useCallback(
    async (action: () => Promise<void>) => {
      setBusy(true);
      try {
        await action();
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'That change did not save.');
      } finally {
        setBusy(false);
      }
    },
    [load]
  );

  const step = (offset: number): void => {
    setWeek((w) => shiftPayrollPeriod(payrollPeriodFor(w), offset).endDate);
  };

  const weekTotals = useMemo(() => {
    const totals = new Map<ProductionLineCode, { batches: number; bonus: number }>();
    for (const d of view?.days ?? []) {
      const t = totals.get(d.line) ?? { batches: 0, bonus: 0 };
      t.batches += d.batches;
      t.bonus += d.bonusUsd;
      totals.set(d.line, t);
    }
    return totals;
  }, [view]);

  // Every day of the week, read or not — a day the job never read must look missing,
  // not like a quiet day.
  const dates = useMemo(() => {
    if (view === null) {
      return [];
    }
    const out: string[] = [];
    for (let d = view.period.startDate; d <= view.period.endDate; d = addDays(d, 1)) {
      out.push(d);
    }
    return out;
  }, [view]);

  const inProgress = view !== null && view.period.endDate >= todayInZone(new Date());

  return (
    <CenterModal open={open} onClose={onClose} title="Production line crews">
      <Caption1 className={styles.intro}>
        Every {BATCHES_PER_LINE_BONUS} batches a line finishes in a day earns the line{' '}
        {money(LINE_BONUS_USD)}, read from the production report at 8:00 each morning. On
        Wednesday morning each line&apos;s week is added to the Bonus report for everyone on
        its crew — the entries still need posting to NetSuite as usual. People stay on a crew
        until someone removes them.
      </Caption1>

      <div className={styles.weekBar}>
        <Button aria-label="Previous week" disabled={busy} onClick={() => step(-1)}>
          ‹ Prev
        </Button>
        <Text weight="semibold" className={styles.weekLabel}>
          {view === null
            ? 'Loading…'
            : `Week ${shortDate(view.period.startDate)} – ${shortDate(view.period.endDate)}${
                inProgress ? ' (in progress)' : ''
              }`}
        </Text>
        <Button
          aria-label="Next week"
          disabled={busy || week >= currentWeek}
          onClick={() => step(1)}
        >
          Next ›
        </Button>
      </div>

      {error !== null && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {view === null ? (
        <Spinner label="Loading crews…" />
      ) : (
        <>
          {view.crews.map((crew) => {
            const onLine = new Set(crew.members.map((m) => m.employeeId));
            const query = (queries[crew.line] ?? '').trim().toLowerCase();
            const candidates = options.employees
              .filter((e) => !onLine.has(e.id))
              .filter((e) => query === '' || e.name.toLowerCase().includes(query))
              .slice(0, 50);
            const totals = weekTotals.get(crew.line);
            const bonus = totals?.bonus ?? 0;
            return (
              <section key={crew.line} className={styles.line} aria-label={crew.label}>
                <div className={styles.lineHead}>
                  <Subtitle2>{crew.label}</Subtitle2>
                  <Caption1 className={styles.week}>
                    {totals?.batches ?? 0} batches ·{' '}
                    <span className={bonus > 0 ? styles.weekEarned : undefined}>
                      {money(bonus)} {inProgress ? 'so far' : ''} per crew member
                    </span>
                  </Caption1>
                </div>
                <div className={styles.members}>
                  {crew.members.length === 0 ? (
                    <Text size={200} className={styles.empty}>
                      Nobody yet — this line&apos;s bonus will not be posted to anyone.
                    </Text>
                  ) : (
                    crew.members.map((m) => (
                      <span key={m.employeeId} className={styles.member}>
                        <Text size={200}>{m.name}</Text>
                        {!m.active && (
                          <Badge
                            size="small"
                            color="warning"
                            title="Deactivated in the directory. They stay on this crew, but are not paid while inactive."
                          >
                            inactive
                          </Badge>
                        )}
                        {m.active && m.departmentName === null && (
                          <Badge
                            size="small"
                            color="danger"
                            title="No department, so the Wednesday post skips this person. Set their department on the Employees tab."
                          >
                            no department
                          </Badge>
                        )}
                        <Button
                          size="small"
                          appearance="subtle"
                          aria-label={`Remove ${m.name} from ${crew.label}`}
                          disabled={busy}
                          onClick={() =>
                            void run(() => api.removeProductionLineMember(crew.line, m.employeeId))
                          }
                        >
                          ✕
                        </Button>
                      </span>
                    ))
                  )}
                </div>
                <Combobox
                  className={styles.add}
                  aria-label={`Add someone to ${crew.label}`}
                  placeholder="Add someone…"
                  disabled={busy}
                  value={queries[crew.line] ?? ''}
                  onChange={(e) => {
                    setQueries((q) => ({ ...q, [crew.line]: e.target.value }));
                  }}
                  onOptionSelect={(_, data) => {
                    const id = data.optionValue;
                    setQueries((q) => ({ ...q, [crew.line]: '' }));
                    if (id !== undefined) {
                      void run(() => api.addProductionLineMember(crew.line, id));
                    }
                  }}
                >
                  {candidates.map((e) => (
                    <Option key={e.id} value={e.id} text={e.name}>
                      {e.name}
                    </Option>
                  ))}
                </Combobox>
              </section>
            );
          })}

          <Subtitle2>Batches read from the production report</Subtitle2>
          <div className={styles.gridWrap}>
            <table className={styles.grid}>
              <thead>
                <tr>
                  <th className={`${styles.th} ${styles.thDate}`}>Day</th>
                  {PRODUCTION_LINES.map((l) => (
                    <th key={l.code} className={styles.th}>
                      {l.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dates.map((date) => (
                  <tr key={date}>
                    <td className={`${styles.td} ${styles.tdDate}`}>{shortDate(date)}</td>
                    {PRODUCTION_LINES.map((l) => {
                      const day = view.days.find((d) => d.date === date && d.line === l.code);
                      if (day === undefined) {
                        return (
                          <td key={l.code} className={`${styles.td} ${styles.notRead}`}>
                            not read
                          </td>
                        );
                      }
                      return (
                        <td key={l.code} className={styles.td}>
                          {day.batches}
                          {day.bonusUsd > 0 && (
                            <span className={styles.earned}> · {money(day.bonusUsd)}</span>
                          )}
                          {day.unfinished > 0 && (
                            <span
                              className={styles.noEnd}
                              title="Rows after the line's last End time are not counted. A missing End time is usually a batch that was made — add it on the production report and it is picked up on Wednesday."
                            >
                              {' '}
                              (+{day.unfinished} no End)
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr>
                  <td className={`${styles.td} ${styles.tdDate} ${styles.total}`}>Week</td>
                  {PRODUCTION_LINES.map((l) => {
                    const t = weekTotals.get(l.code);
                    return (
                      <td key={l.code} className={`${styles.td} ${styles.total}`}>
                        {t?.batches ?? 0} · {money(t?.bonus ?? 0)}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </CenterModal>
  );
}
